import { Option } from '~/components/Select/Select';
import countries from '~/constants/countries';
import { currencies } from '~/constants/currency';
import { INTEGRATED_STORES } from '~/constants/rate-at-checkout';
import { JSONAPIAddress, SavedAPIAddress } from './APIAddress';
import { OrdersRenderData } from './Order';
import Rate from './Rate';

enum TypeId {
  LIVE_RATE = 'LIVE_RATE',
  FLAT_RATE = 'FLAT_RATE',
  FREE_SHIPPING = 'FREE_SHIPPING',
}

interface ShippingOptionPayload {
  object_id?: string;
  name: string;
  description: string;
  flat_rate?: string | number;
  flat_rate_currency?: string;
  is_live_rate?: boolean;
  type: string;
  rate_adjustment?: string | number;
  is_active?: boolean;
  service_levels: Maybe<ShippingOptionServiceLevel[]>;
  rate_adjustment_type?: boolean | null | Option;
  free_shipping_threshold_min?: number;
  free_shipping_threshold_currency?: string;
}

interface ShippingOptionInitialValue {
  name: string;
  description: string;
  flat_rate: string | number;
  flat_rate_currency: string;
  type: Option | undefined;
  rate_adjustment: string | number;
  rate_adjustment_type?: Option;
  flat_rate_fallback?: string | number;
  service_levels?: Option[];
  free_shipping_threshold_min?: string | number;
  free_shipping_threshold_currency: string;
}

type ShippingOptionsList = {
  data?: ShippingOptionPayload[];
  kind: string;
};

type RateAtCheckoutErrorObject = {
  [error: string]: string;
};

type RateAtCheckoutErrorArray = string[];

type ShippingOptionServiceLevel = {
  account_object_id: string;
  service_level_token: string;
};

type ParcelTemplateTemplate = {
  name: string;
  token: string;
  provider_id: number;
  api_token: string;
  is_variable_dimensions: string | null;
  length: string;
  width: string;
  height: string;
  distance_unit: string;
};

export interface BaseJSONParcelTemplate {
  object_id: string;
  length: Maybe<string>;
  width: string;
  height: Maybe<string>;
  distance_unit: string;
  name: string;
  template: ParcelTemplateTemplate | null;
  is_default: boolean;
  weight: string;
  weight_unit: string;
}

function fromJSONsenderAddress(
  jsonAddress: JSONAPIAddress & {
    object_owner_id: number;
    address_id: string;
    country: keyof typeof countries;
  },
): SavedAPIAddress {
  return {
    objectOwnerID: jsonAddress.object_owner_id,
    id: jsonAddress.id as number,
    name: jsonAddress.name,
    email: jsonAddress.email,
    phone: jsonAddress.phone,
    company: jsonAddress.company,
    addressId: jsonAddress.address_id,
    street1: jsonAddress.street1,
    street2: jsonAddress.street2,
    state: jsonAddress.state,
    city: jsonAddress.city,
    zip: jsonAddress.zip,
    country: countries[jsonAddress.country],
    streetNumber: jsonAddress.street_no,
    isDefaultReturn: jsonAddress.is_default_return,
    isDefaultSender: jsonAddress.is_default_sender,
  };
}

function currencyFormatToOptions() {
  let list: Option[] = [];
  for (const currency in currencies) {
    list.push({ label: currency, value: currency });
  }
  return list;
}

function getCurrencySymbol(iso: string | undefined) {
  if (!iso) return;
  return currencies[iso as keyof typeof currencies].symbol;
}

function convertAdjustmentValue(value: string, type: unknown) {
  const intVal = +value;
  return type || intVal === 0 ? intVal : intVal * -1;
}

function formValueToPayload(formValues: ShippingOptionInitialValue): Maybe<ShippingOptionPayload> {
  const {
    name,
    description,
    flat_rate,
    flat_rate_fallback,
    flat_rate_currency,
    type,
    rate_adjustment,
    rate_adjustment_type,
    service_levels,
    free_shipping_threshold_min,
    free_shipping_threshold_currency,
  } = formValues;

  function serviceLevel(service_levels: ShippingOptionInitialValue['service_levels']) {
    if (!Array.isArray(service_levels)) return [];
    return service_levels.map((el) => {
      const parsedValue = (el.value as string).split('|');
      return { account_object_id: parsedValue[0], service_level_token: parsedValue[1] };
    });
  }

  switch (type?.value) {
    case 'FLAT_RATE':
      return {
        name,
        description,
        type: type.value,
        flat_rate: Number(flat_rate),
        // This type seems wrong, `flate_rate_currency` has type of string. Maybe the type is wrong?
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        flat_rate_currency: (flat_rate_currency as any).value,
        service_levels: serviceLevel(service_levels),
      };
    case 'LIVE_RATE':
      // Do it without asserting type or non-nullability?
      const convertedRate = convertAdjustmentValue(
        rate_adjustment as string,
        rate_adjustment_type!.value,
      );
      return {
        name,
        description,
        type: type.value,
        flat_rate: Number(flat_rate_fallback),
        // This type seems wrong, `flate_rate_currency` has type of string. Maybe the type is wrong?
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        flat_rate_currency: (flat_rate_currency as any)?.value,
        rate_adjustment: convertedRate,
        service_levels: serviceLevel(service_levels),
      };
    case 'FREE_SHIPPING':
      return {
        name,
        description,
        type: type.value,
        free_shipping_threshold_min: Number(free_shipping_threshold_min),
        // TODO: This type seems wrong, `flate_rate_currency` has type of string. Maybe the type is wrong?
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        free_shipping_threshold_currency: (free_shipping_threshold_currency as any)?.value,
        service_levels: serviceLevel(service_levels),
      };
    default:
      return;
  }
}

function fromJSONcarrierTemplate(template: ParcelTemplateTemplate | null) {
  if (!template) return null;
  return {
    ...template,
    providerId: template.provider_id,
    apiToken: template.api_token,
    isVariableDimensions: template.is_variable_dimensions,
    distanceUnit: template.distance_unit,
  };
}

function fromJSON(parcelTemplate: BaseJSONParcelTemplate) {
  return {
    objectId: parcelTemplate.object_id,
    height: Number(parcelTemplate.height),
    length: Number(parcelTemplate.length),
    width: Number(parcelTemplate.width),
    isDefault: parcelTemplate.is_default,
    name: parcelTemplate.name,
    distanceUnit: parcelTemplate.distance_unit,
    weightUnit: parcelTemplate.weight_unit,
    weight: Number(parcelTemplate.weight),
    carrierTemplate: fromJSONcarrierTemplate(parcelTemplate.template),
  };
}

function parseErrorObject(errorObj: RateAtCheckoutErrorObject): RateAtCheckoutErrorArray {
  const errors: RateAtCheckoutErrorArray = [];
  for (const [errorKey, errorMessage] of Object.entries(errorObj)) {
    if (errorKey && errorMessage) {
      errors.push(`${errorKey}: ${errorMessage}`);
    }
  }
  return errors;
}

/***
@description get Shipment dataset by shipmentId from datasets. batchEntry and order
 */
function getShipmentDatasetById(shipmentId: string, shipmentDatasets: OrdersRenderData[]) {
  if (!shipmentDatasets || !shipmentId) return;

  return shipmentDatasets.find((dataset) => {
    if (dataset.batchEntry?.shipmentObjectId === shipmentId) {
      return dataset;
    }
  });
}

/***
@description check if order came from an integrated Rate at Checkout store
 */
function isIntegratedStore(dataset: Maybe<OrdersRenderData>) {
  if (!dataset) return;

  const storeName = dataset.order?.shopApp?.toLowerCase().trim();
  return INTEGRATED_STORES.includes(storeName);
}

/***
@description return shipping option if it match integrated store methodName.
 */
function getMatchedShippingOption(
  shippingOptions: ShippingOptionPayload[],
  dataset: Maybe<OrdersRenderData>,
) {
  const shippingMethodName = dataset?.order?.shippingMethod?.toLowerCase().trim();
  if (!shippingMethodName || shippingOptions.length === 0) return;

  // find match for selected Store Options by name in existing user Shipping Options
  return shippingOptions.find((option) => {
    const shippingOptionName = option?.name?.toLowerCase().trim();
    return shippingOptionName === shippingMethodName;
  });
}

/***
 @description find matched service levels with rates.
 @description return cheapest matched rate.
 */
function findCheapestMatchedServiceLevelRate(
  matchedOptionServiceLevels: Maybe<ShippingOptionServiceLevel[]>,
  rates: Rate[],
) {
  if (!matchedOptionServiceLevels || rates.length === 0) return;
  let cheapestMatchedRate;

  for (const serviceLevel of matchedOptionServiceLevels) {
    // find and return matched service level and carrier account id to Rate
    const matchedRate = rates.find((rate) => {
      const optionAccountId = serviceLevel.account_object_id?.replace(/-/g, '');
      const optionToken = serviceLevel.service_level_token;
      const isMatchedAccount = rate.carrierAccount === optionAccountId;
      const isMatchedToken = rate.servicelevel?.token === optionToken;

      return isMatchedToken && isMatchedAccount;
    });

    // found matched service level token with rate
    if (matchedRate) {
      if (!cheapestMatchedRate) {
        // set first matched Rate
        cheapestMatchedRate = matchedRate;
      } else {
        const matchedRateAmount = parseFloat(matchedRate.amount);
        const cheapestRateAmount = parseFloat(cheapestMatchedRate.amount);
        cheapestMatchedRate =
          matchedRateAmount < cheapestRateAmount ? matchedRate : cheapestMatchedRate;
      }
    }
  }
  return cheapestMatchedRate;
}

export {
  formValueToPayload,
  currencyFormatToOptions,
  getCurrencySymbol,
  fromJSON,
  fromJSONsenderAddress,
  parseErrorObject,
  isIntegratedStore,
  getShipmentDatasetById,
  getMatchedShippingOption,
  findCheapestMatchedServiceLevelRate,
  ShippingOptionPayload,
  ShippingOptionsList,
  ShippingOptionInitialValue,
  ShippingOptionServiceLevel,
  TypeId,
  RateAtCheckoutErrorObject,
  RateAtCheckoutErrorArray,
};
